import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";
import { RootState } from "store/store";

export type ConfigMap = Record<string, unknown>;

export interface IPlatformConfig {
  id: number;
  name: string;
  version: string;
  config: ConfigMap;
  createdOn?: string;
  modifiedOn?: string;
  createdBy?: number;
  modifiedBy?: number;
  active?: number;
}

export interface IPlatformConfigDetail {
  name: string;
  version: string;
  config: ConfigMap;
}

export interface IUpdatePlatformConfigPayload {
  id: number;
  name: string;
  version: string;
  config: ConfigMap;
}

export interface ICreatePlatformConfigPayload {
  name: string;
  version: string;
  config: ConfigMap;
}

type Status = "idle" | "loading" | "saving" | "success" | "error";

interface PlatformConfigState {
  // Listing (per selected platform)
  listStatus: Status;
  listError?: string;
  platform?: string;
  configs: IPlatformConfig[];
  // Detail (selected version being edited)
  detailStatus: Status;
  detailError?: string;
  detail?: IPlatformConfigDetail;
  // Save/create operations
  saveStatus: Status;
  saveError?: string;
}

const initialState: PlatformConfigState = {
  listStatus: "idle",
  configs: [],
  detailStatus: "idle",
  saveStatus: "idle",
};

export const getPlatformConfigs = createAsyncThunk<IPlatformConfig[], string>(
  "platformConfig/list/get",
  async (platform, thunkApi) => {
    try {
      const response = await API.get<IPlatformConfig[]>(apis.platformConfig, {
        params: { name: platform },
      });
      return response.data ?? [];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to load configs";
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getPlatformConfigDetail = createAsyncThunk<
  IPlatformConfigDetail,
  { name: string; version: string }
>("platformConfig/detail/get", async ({ name, version }, thunkApi) => {
  try {
    const response = await API.get<IPlatformConfigDetail>(
      `${apis.platformConfig}/${encodeURIComponent(name)}/${encodeURIComponent(version)}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || "Failed to load config detail";
    return thunkApi.rejectWithValue({ message: errorMessage });
  }
});

export const updatePlatformConfig = createAsyncThunk<
  IUpdatePlatformConfigPayload,
  IUpdatePlatformConfigPayload
>("platformConfig/update", async (payload, thunkApi) => {
  try {
    const { id, ...body } = payload;
    await API.put(`${apis.platformConfig}/${id}`, body);
    return payload;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || "Failed to update config";
    return thunkApi.rejectWithValue({ message: errorMessage });
  }
});

export const createPlatformConfig = createAsyncThunk<
  ICreatePlatformConfigPayload,
  ICreatePlatformConfigPayload
>("platformConfig/create", async (payload, thunkApi) => {
  try {
    await API.post(apis.platformConfig, payload);
    return payload;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || "Failed to create config";
    return thunkApi.rejectWithValue({ message: errorMessage });
  }
});

const platformConfigSlice = createSlice({
  name: "platformConfig",
  initialState,
  reducers: {
    clearConfigDetail: (state) => {
      state.detailStatus = "idle";
      state.detailError = undefined;
      state.detail = undefined;
      state.saveStatus = "idle";
      state.saveError = undefined;
    },
    resetSaveStatus: (state) => {
      state.saveStatus = "idle";
      state.saveError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPlatformConfigs.pending, (state, action) => {
        state.listStatus = "loading";
        state.listError = undefined;
        state.platform = action.meta.arg;
      })
      .addCase(getPlatformConfigs.fulfilled, (state, action) => {
        state.listStatus = "success";
        state.configs = action.payload;
      })
      .addCase(getPlatformConfigs.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.listStatus = "error";
        state.listError = error?.message;
        state.configs = [];
      })
      .addCase(getPlatformConfigDetail.pending, (state) => {
        state.detailStatus = "loading";
        state.detailError = undefined;
        state.detail = undefined;
      })
      .addCase(getPlatformConfigDetail.fulfilled, (state, action) => {
        state.detailStatus = "success";
        state.detail = action.payload;
      })
      .addCase(getPlatformConfigDetail.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.detailStatus = "error";
        state.detailError = error?.message;
      })
      .addCase(updatePlatformConfig.pending, (state) => {
        state.saveStatus = "saving";
        state.saveError = undefined;
      })
      .addCase(updatePlatformConfig.fulfilled, (state, action) => {
        state.saveStatus = "success";
        // Keep the detail and cached list row in sync with what was saved.
        if (state.detail) {
          state.detail = { ...state.detail, config: action.payload.config };
        }
        state.configs = state.configs.map((item) =>
          item.id === action.payload.id ? { ...item, config: action.payload.config } : item
        );
      })
      .addCase(updatePlatformConfig.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.saveStatus = "error";
        state.saveError = error?.message;
      })
      .addCase(createPlatformConfig.pending, (state) => {
        state.saveStatus = "saving";
        state.saveError = undefined;
      })
      .addCase(createPlatformConfig.fulfilled, (state) => {
        state.saveStatus = "success";
      })
      .addCase(createPlatformConfig.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.saveStatus = "error";
        state.saveError = error?.message;
      });
  },
});

export const { clearConfigDetail, resetSaveStatus } = platformConfigSlice.actions;
export const selectPlatformConfigState = (state: RootState) => state.platformConfig;
export default platformConfigSlice.reducer;
